import { TileType, isApple, isSandTile, isExpandedKiliFruit } from '@blockworld/shared';

export interface MutableTileAccessor {
    getTile(x: number, y: number): TileType;
    setLocalTile(x: number, y: number, type: TileType): void;
}

export class FallingBlockPredictor {
    public localFallingBlocks: Set<string> = new Set();
    public freshFallingBlocks: Set<string> = new Set();
    public logInertia: Map<string, number> = new Map();

    constructor(private readonly tiles: MutableTileAccessor) {}

    public tickLocalFallingBlocks(): void {
        const toRemove: string[] = [];
        const toAdd: string[] = [];
        const movedThisTick = new Set<string>(); // Keys of blocks that moved this tick
        // Live set of falling block positions, mirroring server's fallingSet.
        // Updated in real-time as blocks move/stabilize so subsequent blocks
        // in the same tick see correct support state.
        const fallingSet = new Set<string>(this.localFallingBlocks);

        // Sort ALL blocks by Y descending (bottom = highest Y first).
        // Single unified pass: falls and rolls interleave by Y level.
        const sortedBlocks = Array.from(this.localFallingBlocks)
            .map(key => {
                const [x, y] = key.split(',').map(Number);
                return { key, x, y };
            })
            .sort((a, b) => b.y - a.y);

        for (const { key, x, y } of sortedBlocks) {
            const tile = this.tiles.getTile(x, y);
            if (!this.isFallingType(tile)) {
                toRemove.push(key);
                fallingSet.delete(key);
                continue;
            }

            const belowTile = this.tiles.getTile(x, y + 1);
            const belowKey = `${x},${y + 1}`;

            // --- VERTICAL FALL ---
            if (belowTile === TileType.SKY) {
                this.tiles.setLocalTile(x, y, TileType.SKY);
                this.tiles.setLocalTile(x, y + 1, tile);
                toRemove.push(key);
                const newKey = `${x},${y + 1}`;
                toAdd.push(newKey);
                movedThisTick.add(newKey);
                fallingSet.delete(key);
                fallingSet.add(newKey);

                if (tile === TileType.LOG) {
                    const inertia = this.logInertia.get(key);
                    this.logInertia.delete(key);
                    if (inertia !== undefined) {
                        this.logInertia.set(newKey, inertia);
                    }
                }

                const aboveY = y - 1;
                const aboveTile = this.tiles.getTile(x, aboveY);
                if (this.isFallingType(aboveTile)) {
                    const aboveKey = `${x},${aboveY}`;
                    if (!fallingSet.has(aboveKey)) {
                        toAdd.push(aboveKey);
                        fallingSet.add(aboveKey);
                    }
                }
                continue;
            }

            // --- LEAVES EVAPORATION ---
            if (belowTile === TileType.LEAVES) {
                let leafEnd = y + 1;
                while (this.tiles.getTile(x, leafEnd + 1) === TileType.LEAVES) {
                    leafEnd++;
                }

                const belowStackTile = this.tiles.getTile(x, leafEnd + 1);
                const stackIsSupported = belowStackTile !== TileType.SKY;

                if (!stackIsSupported) {
                    // Unsupported leaves — evaporate central column to sticks/apples
                    const evaporatedRows: number[] = [];
                    for (let ly = y + 1; ly <= leafEnd; ly++) {
                        const evapType = isApple(x, ly) ? TileType.HEART_CONTAINER : TileType.STICK;
                        this.tiles.setLocalTile(x, ly, evapType);
                        const stickKey = `${x},${ly}`;
                        if (!fallingSet.has(stickKey)) {
                            toAdd.push(stickKey);
                            fallingSet.add(stickKey);
                        }
                        evaporatedRows.push(ly);
                    }
                    // Check connected branches for trunk support
                    const branchEvaporations: { bx: number; by: number }[] = [];
                    for (const rowY of evaporatedRows) {
                        if (!this.hasTrunkSupportInDirection(x, rowY, 1)) {
                            for (let dx = 1; dx <= 3; dx++) {
                                const lx = x + dx;
                                if (this.tiles.getTile(lx, rowY) !== TileType.LEAVES) break;
                                branchEvaporations.push({ bx: lx, by: rowY });
                            }
                        }
                        if (!this.hasTrunkSupportInDirection(x, rowY, -1)) {
                            for (let dx = 1; dx <= 3; dx++) {
                                const lx = x - dx;
                                if (this.tiles.getTile(lx, rowY) !== TileType.LEAVES) break;
                                branchEvaporations.push({ bx: lx, by: rowY });
                            }
                        }
                    }
                    for (const pos of branchEvaporations) {
                        const evapType = isApple(pos.bx, pos.by) ? TileType.HEART_CONTAINER : TileType.STICK;
                        this.tiles.setLocalTile(pos.bx, pos.by, evapType);
                        const branchKey = `${pos.bx},${pos.by}`;
                        if (!fallingSet.has(branchKey)) {
                            toAdd.push(branchKey);
                            fallingSet.add(branchKey);
                        }
                    }
                    continue;
                }
                // Supported leaves — fall through to rolling/stabilize below
            }

            // --- BLOCKED: check support stability ---
            if (this.isFallingType(belowTile)) {
                if (fallingSet.has(belowKey)) {
                    // Resting on another falling block — stay synced
                    continue;
                }
                // Below is a stable falling-type block — fall through to roll/stabilize
            }

            // --- LOG ROLLING: try before stabilizing ---
            if (tile === TileType.LOG) {
                let dir = this.logInertia.get(key);
                if (dir === undefined) {
                    dir = (y % 2 === 0) ? 1 : -1;
                }

                let rolled = false;
                for (const tryDir of [dir, -dir]) {
                    const diagX = x + tryDir;
                    const diagY = y + 1;
                    if (this.tiles.getTile(diagX, y) === TileType.SKY &&
                        this.tiles.getTile(diagX, diagY) === TileType.SKY) {
                        this.tiles.setLocalTile(x, y, TileType.SKY);
                        this.tiles.setLocalTile(diagX, diagY, TileType.LOG);
                        this.logInertia.delete(key);
                        toRemove.push(key);

                        fallingSet.delete(key);

                        const aboveTile = this.tiles.getTile(x, y - 1);
                        if (this.isFallingType(aboveTile)) {
                            const aboveKey = `${x},${y - 1}`;
                            if (!fallingSet.has(aboveKey)) {
                                toAdd.push(aboveKey);
                                fallingSet.add(aboveKey);
                            }
                        }

                        const newKey = `${diagX},${diagY}`;
                        this.logInertia.set(newKey, tryDir);
                        toAdd.push(newKey);
                        movedThisTick.add(newKey);
                        fallingSet.add(newKey);

                        rolled = true;
                        break;
                    }
                }
                if (rolled) continue;
                this.logInertia.delete(key);
            }

            // --- STABILIZE ---
            toRemove.push(key);
            fallingSet.delete(key);
        }

        // PROPAGATED LOG ROLLING: LOGs added to toAdd by upward propagation
        // that are on stable ground and didn't move this tick
        const landingCandidates: { key: string; x: number; y: number }[] = [];
        for (const key of toAdd) {
            if (movedThisTick.has(key)) continue;
            const [ax, ay] = key.split(',').map(Number);
            if (this.tiles.getTile(ax, ay) !== TileType.LOG) continue;
            const belowTile = this.tiles.getTile(ax, ay + 1);
            if (belowTile === TileType.SKY) continue;
            const bk = `${ax},${ay + 1}`;
            if (fallingSet.has(bk)) continue;
            landingCandidates.push({ key, x: ax, y: ay });
        }
        landingCandidates.sort((a, b) => b.y - a.y);

        for (const { key, x: lx, y: ly } of landingCandidates) {
            if (this.tiles.getTile(lx, ly) !== TileType.LOG) continue;

            let dir = this.logInertia.get(key);
            if (dir === undefined) {
                dir = (ly % 2 === 0) ? 1 : -1;
            }

            let rolled = false;
            for (const tryDir of [dir, -dir]) {
                const diagX = lx + tryDir;
                const diagY = ly + 1;
                if (this.tiles.getTile(diagX, ly) === TileType.SKY &&
                    this.tiles.getTile(diagX, diagY) === TileType.SKY) {
                    this.tiles.setLocalTile(lx, ly, TileType.SKY);
                    this.tiles.setLocalTile(diagX, diagY, TileType.LOG);
                    this.logInertia.delete(key);
                    fallingSet.delete(key);

                    const aboveTile = this.tiles.getTile(lx, ly - 1);
                    if (this.isFallingType(aboveTile)) {
                        const aboveKey = `${lx},${ly - 1}`;
                        if (!fallingSet.has(aboveKey)) {
                            toAdd.push(aboveKey);
                            fallingSet.add(aboveKey);
                        }
                    }

                    const addIdx = toAdd.indexOf(key);
                    if (addIdx !== -1) {
                        toAdd.splice(addIdx, 1);
                    }

                    const newKey = `${diagX},${diagY}`;
                    this.logInertia.set(newKey, tryDir);
                    toAdd.push(newKey);
                    fallingSet.add(newKey);

                    rolled = true;
                    break;
                }
            }
            if (!rolled) {
                this.logInertia.delete(key);
            }
        }

        // Replace localFallingBlocks with the live-updated fallingSet.
        // fallingSet was maintained in real-time throughout the tick (deletes on
        // stabilize/move-from, adds on move-to/propagation), so it already
        // reflects the correct final state — no need for deferred toRemove/toAdd.
        this.localFallingBlocks = fallingSet;

        // Promote fresh blocks to active falling set for next tick's processing.
        // This gives newly-created blocks (e.g. from tree chop) a 1-tick grace period
        // so the whole tree visually changes tile types before anything moves.
        for (const key of this.freshFallingBlocks) {
            this.localFallingBlocks.add(key);
        }
        this.freshFallingBlocks.clear();
    }

    public isFallingType(tileType: TileType): boolean {
        return tileType === TileType.DIRT || tileType === TileType.GRASS || tileType === TileType.SKULL || tileType === TileType.STICK || tileType === TileType.LOG || tileType === TileType.FLINT || tileType === TileType.HEART_CONTAINER || tileType === TileType.WOODEN_SHIELD || tileType === TileType.STONE_CLUB || tileType === TileType.IRON_SWORD || tileType === TileType.BOOTS
            || tileType === TileType.KILI_AKESI || tileType === TileType.KILI_ALE || tileType === TileType.KILI_IKE || tileType === TileType.KILI_JELO || tileType === TileType.KILI_KEN || tileType === TileType.KILI_KIWEN || tileType === TileType.KILI_KULUPU || tileType === TileType.KILI_LAWA || tileType === TileType.KILI_SOWELI
            || isSandTile(tileType)
            || isExpandedKiliFruit(tileType);
    }

    public propagateLocalInstabilityUp(x: number, y: number): void {
        const tile = this.tiles.getTile(x, y);
        if (!this.isFallingType(tile)) return;
        const key = `${x},${y}`;
        if (!this.localFallingBlocks.has(key)) {
            this.localFallingBlocks.add(key);
        }
        this.propagateLocalInstabilityUp(x, y - 1);
    }

    private hasTrunkSupportInDirection(centerX: number, rowY: number, dir: number): boolean {
        for (let dx = 1; dx <= 3; dx++) {
            const lx = centerX + dx * dir;
            if (this.tiles.getTile(lx, rowY) !== TileType.LEAVES) break;

            let checkY = rowY + 1;
            while (this.tiles.getTile(lx, checkY) === TileType.LEAVES) {
                checkY++;
            }
            if (this.tiles.getTile(lx, checkY) === TileType.TRUNK) {
                return true;
            }
        }
        return false;
    }
}
